export const LOWEST_ELEVATION_EPSILON_DEG = 0.05;
export const LOWEST_SWEEP_DUP_GAP_SECONDS = 60;
const SPLIT_CUT_GATE_RATIO = 0.15;
export const MIN_SWEEP_AZIMUTHS = 360;
export const MAX_AZIMUTH_GAP_DEG = 3;

function sweepGateCount(sweep) {
  return sweep?.extracted?.numGates || 0;
}

function preferSweepIndex(sweeps, aIdx, bIdx) {
  const aValid = sweeps[aIdx]?.validCount || 0;
  const bValid = sweeps[bIdx]?.validCount || 0;
  if (bValid !== aValid) return bValid > aValid ? bIdx : aIdx;
  const aGates = sweepGateCount(sweeps[aIdx]);
  const bGates = sweepGateCount(sweeps[bIdx]);
  if (bGates !== aGates) return bGates > aGates ? bIdx : aIdx;
  return bIdx;
}

function isSplitCutPair(a, b) {
  const aGates = sweepGateCount(a);
  const bGates = sweepGateCount(b);
  if (aGates < 1 || bGates < 1) return false;
  return Math.abs(aGates - bGates) / Math.max(aGates, bGates) >= SPLIT_CUT_GATE_RATIO;
}

export function parseScanKey(key) {
  const filename = String(key || "").split("/").pop() || "";
  const match = filename.match(/([A-Z]{4})(\d{8})[_-](\d{6})(?:[_.-]|$)/);
  if (match) return { station: match[1], date: match[2], time: match[3] };
  return { station: null, date: null, time: null };
}

export function parseScanTime(filename) {
  const parsed = parseScanKey(filename);
  if (parsed.time) {
    return `${parsed.time.slice(0, 2)}:${parsed.time.slice(2, 4)}:${parsed.time.slice(4, 6)}`;
  }
  const match = String(filename).match(/_(\d{6})(?:_|\.|$)/);
  if (!match) return null;
  const t = match[1];
  return `${t.slice(0, 2)}:${t.slice(2, 4)}:${t.slice(4, 6)}`;
}

export function parseL3KeyTime(key) {
  const withSec = String(key).match(/_(\d{4})_(\d{2})_(\d{2})_(\d{2})_(\d{2})_(\d{2})$/);
  if (withSec) return `${withSec[4]}:${withSec[5]}:${withSec[6]}`;
  const noSec = String(key).match(/_(\d{4})_(\d{2})_(\d{2})_(\d{2})_(\d{2})$/);
  if (noSec) return `${noSec[4]}:${noSec[5]}:00`;
  return null;
}

export function timeToSeconds(t) {
  const parts = String(t || "").split(":");
  return (
    (parseInt(parts[0] || "0", 10) * 3600) +
    (parseInt(parts[1] || "0", 10) * 60) +
    (parseInt(parts[2] || "0", 10) || 0)
  );
}

export function findClosestByTime(items, targetTime, maxDeltaSec = 600) {
  if (!items.length) return null;
  const targetSec = timeToSeconds(targetTime);
  let best = null;
  let bestDelta = Infinity;
  for (const item of items) {
    const sec = timeToSeconds(item.time);
    let delta = Math.abs(sec - targetSec);
    delta = Math.min(delta, 86400 - delta);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = item;
    }
  }
  if (best && bestDelta <= maxDeltaSec) return best;
  return null;
}

export function listAvailableElevations(sweeps) {
  const rounded = sweeps
    .map((s) => s.elevation)
    .filter((v) => Number.isFinite(v))
    .map((v) => Math.round(v * 10) / 10)
    .sort((a, b) => a - b);
  return [...new Set(rounded)];
}

export function getLowElevationSweepIndices(sweeps) {
  const finite = sweeps.filter((s) => Number.isFinite(s.elevation));
  if (!finite.length) return [];
  const minElev = Math.min(...finite.map((s) => Math.round(s.elevation * 10) / 10));
  return sweeps
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => Number.isFinite(s.elevation) && Math.abs(Math.round(s.elevation * 10) / 10 - minElev) <= LOWEST_ELEVATION_EPSILON_DEG)
    .map(({ i }) => i);
}

export function findSweepsForElevation(sweeps, elevation) {
  const finite = sweeps
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => Number.isFinite(s.elevation));
  if (!finite.length) return { selected: null, indices: [] };
  const req = Number(elevation);
  let nearest = finite[0];
  for (const item of finite) {
    if (Math.abs(item.s.elevation - req) < Math.abs(nearest.s.elevation - req)) {
      nearest = item;
    }
  }
  const selected = nearest.s.elevation;
  const indices = finite
    .filter(({ s }) => Math.abs(s.elevation - selected) <= LOWEST_ELEVATION_EPSILON_DEG)
    .map(({ i }) => i);
  return { selected, indices };
}

export function dedupeLowestSweeps(sweeps, indices) {
  if (indices.length <= 1) return indices;
  const paired = [];
  for (let i = 0; i < indices.length;) {
    const idx = indices[i];
    const next = indices[i + 1];
    if (next != null && isSplitCutPair(sweeps[idx], sweeps[next])) {
      paired.push(preferSweepIndex(sweeps, idx, next));
      i += 2;
    } else {
      paired.push(idx);
      i += 1;
    }
  }
  if (paired.length <= 1) return paired;
  const selected = [];
  for (const idx of paired) {
    const sweep = sweeps[idx];
    const ts = sweep.timestampMs != null ? sweep.timestampMs / 1000 : null;
    const valid = sweep.validCount || 0;
    if (!selected.length) {
      selected.push({ idx, ts, valid });
      continue;
    }
    const prev = selected[selected.length - 1];
    const nearDup = ts != null && prev.ts != null && Math.abs(ts - prev.ts) <= LOWEST_SWEEP_DUP_GAP_SECONDS;
    if (nearDup) {
      if (preferSweepIndex(sweeps, prev.idx, idx) === idx) {
        selected[selected.length - 1] = { idx, ts, valid };
      }
    } else {
      selected.push({ idx, ts, valid });
    }
  }
  return selected.map((item) => item.idx);
}

export function pickBestSweepByValidity(sweeps, indices) {
  if (!indices.length) throw new Error("No matching sweeps available for requested elevation");
  let best = indices[0];
  let bestValid = -1;
  for (const idx of indices) {
    const valid = sweeps[idx]?.validCount || 0;
    if (valid > bestValid) {
      bestValid = valid;
      best = idx;
    }
  }
  return best;
}

/** Prefer sweeps near targetElevation, breaking ties by valid gate count. */
export function pickBestSweepNearElevation(sweeps, indices, targetElevation) {
  if (!indices.length) throw new Error("No matching sweeps available for requested elevation");
  const target = Number.isFinite(targetElevation) ? targetElevation : sweeps[indices[0]]?.elevation;
  let best = null;
  let bestDist = Infinity;
  let bestValid = -1;
  for (const idx of indices) {
    const sweep = sweeps[idx];
    const valid = sweep?.validCount || 0;
    if (valid <= 0) continue;
    const dist = Number.isFinite(target) && Number.isFinite(sweep?.elevation)
      ? Math.abs(sweep.elevation - target)
      : 0;
    if (
      best == null
      || dist < bestDist - LOWEST_ELEVATION_EPSILON_DEG
      || (Math.abs(dist - bestDist) <= LOWEST_ELEVATION_EPSILON_DEG && valid > bestValid)
    ) {
      best = idx;
      bestDist = dist;
      bestValid = valid;
    }
  }
  return best == null ? pickBestSweepByValidity(sweeps, indices) : best;
}

export function formatTimeParts(dateObj) {
  const hh = String(dateObj.getUTCHours()).padStart(2, "0");
  const mm = String(dateObj.getUTCMinutes()).padStart(2, "0");
  const ss = String(dateObj.getUTCSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

export function localTimeFor(dateObj, timeZone) {
  if (!timeZone) {
    return {
      time_local: "",
      date_local: "",
      timezone: "",
      timezone_abbr: "",
    };
  }
  try {
    const timeFmt = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    const dateFmt = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const abbrFmt = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "short",
    });
    const abbrParts = abbrFmt.formatToParts(dateObj);
    const abbr = abbrParts.find((p) => p.type === "timeZoneName")?.value || "";
    return {
      time_local: timeFmt.format(dateObj),
      date_local: dateFmt.format(dateObj),
      timezone: timeZone,
      timezone_abbr: abbr,
    };
  } catch {
    return {
      time_local: "",
      date_local: "",
      timezone: timeZone,
      timezone_abbr: "",
    };
  }
}

export function buildBounds(lat, lon, maxRangeKm) {
  const dlat = maxRangeKm / 111;
  const dlon = maxRangeKm / (111 * Math.cos((lat * Math.PI) / 180) || 1);
  return {
    south: lat - dlat,
    north: lat + dlat,
    west: lon - dlon,
    east: lon + dlon,
  };
}

export function azimuthSweepComplete(azimuths, {
  minAzimuths = MIN_SWEEP_AZIMUTHS,
  maxGapDeg = MAX_AZIMUTH_GAP_DEG,
} = {}) {
  if (!Array.isArray(azimuths) || azimuths.length < minAzimuths) return false;
  const sorted = [];
  for (let i = 0; i < azimuths.length; i++) {
    const az = Number(azimuths[i]);
    if (Number.isFinite(az)) sorted.push(((az % 360) + 360) % 360);
  }
  if (sorted.length < minAzimuths) return false;
  sorted.sort((a, b) => a - b);
  let maxGap = 0;
  for (let i = 1; i < sorted.length; i++) {
    maxGap = Math.max(maxGap, sorted[i] - sorted[i - 1]);
  }
  maxGap = Math.max(maxGap, sorted[0] + 360 - sorted[sorted.length - 1]);
  return maxGap <= maxGapDeg;
}
